declare global {
  interface Window {
    rdt?: (...args: unknown[]) => void;
  }
}

export type RedditEventType =
  | 'PageVisit'
  | 'ViewContent'
  | 'Search'
  | 'AddToCart'
  | 'AddToWishlist'
  | 'Purchase'
  | 'Lead'
  | 'SignUp'
  | 'Custom';

export interface RedditEventData {
  currency?: string;
  value?: number;
  transactionId?: string;
  itemCount?: number;
  customEventName?: string;
  products?: Array<{
    id?: string;
    name?: string;
    category?: string;
  }>;
}

class RedditPixel {
  private static instance: RedditPixel;

  private constructor() {}

  public static getInstance(): RedditPixel {
    if (!RedditPixel.instance) {
      RedditPixel.instance = new RedditPixel();
    }
    return RedditPixel.instance;
  }

  private isAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.rdt === 'function';
  }

  public track(event: RedditEventType, data?: RedditEventData): void {
    if (!this.isAvailable()) {
      if (import.meta.env.DEV) {
        console.log('[Reddit Pixel] Not available, would track:', event, data);
      }
      return;
    }

    try {
      if (data) {
        window.rdt!('track', event, data);
      } else {
        window.rdt!('track', event);
      }

      if (import.meta.env.DEV) {
        console.log('[Reddit Pixel] Tracked:', event, data);
      }
    } catch (error) {
      console.error('[Reddit Pixel] Error tracking event:', error);
    }
  }

  public trackSignUp(): void {
    this.track('SignUp');
  }

  public trackLead(): void {
    this.track('Lead');
  }

  public trackPurchase(value: number, transactionId?: string): void {
    this.track('Purchase', {
      currency: 'USD',
      value,
      transactionId,
      itemCount: 1
    });
  }

  public trackViewContent(contentName?: string): void {
    this.track('ViewContent', contentName ? {
      products: [{ name: contentName }]
    } : undefined);
  }

  public trackCustom(eventName: string, data?: Omit<RedditEventData, 'customEventName'>): void {
    this.track('Custom', {
      ...data,
      customEventName: eventName
    });
  }
}

export const redditPixel = RedditPixel.getInstance();
